import React, { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import NoIndexRoute from "./components/common/NoIndexRoute";

// Only import Index page synchronously (critical for FCP/LCP)
import Index from "./pages/Index";

// Lazy load all other routes to reduce initial bundle size and improve FID
const Resources = lazy(() => import('./pages/Resources'));
const ROICalculatorPage = lazy(() => import('./pages/ROICalculatorPage'));
const ScanToBIMGuide = lazy(() => import('./pages/ScanToBIMGuide'));
const CaseStudies = lazy(() => import('./pages/CaseStudies'));
const BuyersGuide = lazy(() => import('./pages/BuyersGuide'));
const ResidentialFAQ = lazy(() => import('./pages/ResidentialFAQ'));
const CommercialFAQ = lazy(() => import('./pages/CommercialFAQ'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Services = lazy(() => import("./pages/Services"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Pricing = lazy(() => import("./pages/Pricing"));
const AECServices = lazy(() => import("./pages/AECServices"));
const Hospitality = lazy(() => import("./pages/Hospitality"));
const LegalInsurance = lazy(() => import("./pages/LegalInsurance"));
const PhotoVideoServices = lazy(() => import("./pages/PhotoVideoServices"));
const Industries = lazy(() => import("./pages/Industries"));
const CommercialPricing = lazy(() => import("./pages/CommercialPricing"));
const ResidentialServices = lazy(() => import("./pages/ResidentialServices"));
const ConstructionServices = lazy(() => import("./pages/ConstructionServices"));
const Construction3DServices = lazy(() => import("./pages/Construction3DServices"));
const CommercialRealEstate = lazy(() => import("./pages/CommercialRealEstate"));
const RealtorBranding = lazy(() => import("./pages/RealtorBranding"));
const DigitalTwinSolution = lazy(() => import("./pages/DigitalTwinSolution"));
const AirbnbLanding = lazy(() => import("./pages/AirbnbLanding"));
const TourMyLanding = lazy(() => import("./pages/TourMyLanding"));
const QuickTourServices = lazy(() => import("./pages/QuickTourServices"));
const PlumberLanding = lazy(() => import("./pages/PlumberLanding"));
const ElectricianLanding = lazy(() => import("./pages/ElectricianLanding"));
const GeneralContractors = lazy(() => import("./pages/GeneralContractors"));
const HVACLanding = lazy(() => import("./pages/HVACLanding"));
const MEPEngineersLanding = lazy(() => import("./pages/MEPEngineersLanding"));
const HotelsResorts = lazy(() => import("./pages/HotelsResorts"));
const MatterportOverlayDemo = lazy(() => import("./pages/MatterportOverlayDemo"));
const SiteSurveyLOD100 = lazy(() => import("./pages/SiteSurveyLOD100"));
const PropertyDemo = lazy(() => import("./pages/PropertyDemo"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const ServiceAreaCity = lazy(() => import("./pages/ServiceAreaCity"));
// Legacy CRM imports removed - portal is the new client interface
const RealtorHeadshotOffer = lazy(() => import("./pages/RealtorHeadshotOffer"));
const HomeInsurance199 = lazy(() => import("./pages/HomeInsurance199"));
const RevOpsLanding = lazy(() => import("./pages/RevOpsLanding"));
const CostSegregation = lazy(() => import("./pages/CostSegregation"));
const RetailProductPlacement = lazy(() => import("./pages/RetailProductPlacement"));
const MatterportNashville = lazy(() => import("./pages/MatterportNashville"));
const InsuranceDocumentationNashville = lazy(() => import("./pages/InsuranceDocumentationNashville"));
const ConstructionDocumentationNashville = lazy(() => import("./pages/ConstructionDocumentationNashville"));
const RealEstate3DToursNashville = lazy(() => import("./pages/RealEstate3DToursNashville"));
const StormDamageDocumentation = lazy(() => import("./pages/StormDamageDocumentation"));
const ProjectStatus = lazy(() => import("./pages/ProjectStatus"));
const CommercialLeasing = lazy(() => import("./pages/CommercialLeasing"));
const DisasterDocumentation = lazy(() => import("./pages/DisasterDocumentation"));
const Insurance = lazy(() => import("./pages/Insurance"));
const LawFirms = lazy(() => import("./pages/LawFirms"));
const PropertyManagers = lazy(() => import("./pages/PropertyManagers"));
const BotDebugTool = lazy(() => import("./pages/BotDebugTool"));
const Enterprise = lazy(() => import("./pages/Enterprise"));
const SocialProof = lazy(() => import("./pages/SocialProof"));
const DesignSystem = lazy(() => import("./pages/DesignSystem"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Portal Pages
const PortalLogin = lazy(() => import("./pages/portal/Login"));
const PortalSignup = lazy(() => import("./pages/portal/Signup"));
const PortalForgotPassword = lazy(() => import("./pages/portal/ForgotPassword"));
const PortalResetPassword = lazy(() => import("./pages/portal/ResetPassword"));
const PortalDashboard = lazy(() => import("./pages/portal/Dashboard"));
const PortalProjects = lazy(() => import("./pages/portal/Projects"));
const PortalNewProject = lazy(() => import("./pages/portal/NewProjectRequest"));
const PortalProjectDetail = lazy(() => import("./pages/portal/ProjectDetail"));
const PortalFiles = lazy(() => import("./pages/portal/Files"));
const PortalMessages = lazy(() => import("./pages/portal/Messages"));
const PortalInvoices = lazy(() => import("./pages/portal/Invoices"));
const PortalProfile = lazy(() => import("./pages/portal/Profile"));
const PortalLeads = lazy(() => import("./pages/portal/Leads"));

// Initialize QueryClient with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000,
      gcTime: 300000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Memoized loading fallback
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

type AppProps = {
  // Lets the build-time prerenderer swap in a StaticRouter; the browser uses BrowserRouter.
  Router?: React.ComponentType<{ children?: React.ReactNode }>;
};

const App = ({ Router = BrowserRouter }: AppProps) => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Router>
          <ScrollToTop />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/services" element={<Services />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/analytics" element={<NoIndexRoute><Analytics /></NoIndexRoute>} />
                <Route path="/roi-calculator" element={<ROICalculatorPage />} />
                <Route path="/resources/scan-to-bim-guide" element={<ScanToBIMGuide />} />
                <Route path="/resources/case-studies" element={<CaseStudies />} />
                <Route path="/resources/buyers-guide" element={<BuyersGuide />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/aec-services" element={<AECServices />} />
                <Route path="/hospitality" element={<Hospitality />} />
                <Route path="/legal-insurance" element={<LegalInsurance />} />
                <Route path="/photo-video-services" element={<PhotoVideoServices />} />
                <Route path="/industries" element={<Industries />} />
                <Route path="/commercial-pricing" element={<CommercialPricing />} />
                <Route path="/residential-services" element={<ResidentialServices />} />
                <Route path="/construction-services" element={<ConstructionServices />} />
                <Route path="/construction-3d-services" element={<Construction3DServices />} />
                <Route path="/commercial-real-estate" element={<CommercialRealEstate />} />
                <Route path="/realtor-branding" element={<RealtorBranding />} />
                <Route path="/digital-twin-solution" element={<DigitalTwinSolution />} />
                <Route path="/airbnb" element={<AirbnbLanding />} />
                <Route path="/tourmy" element={<TourMyLanding />} />
                <Route path="/quick-tour-services" element={<QuickTourServices />} />
                <Route path="/plumbers" element={<PlumberLanding />} />
                <Route path="/electricians" element={<ElectricianLanding />} />
                <Route path="/general-contractors" element={<GeneralContractors />} />
                <Route path="/hvac" element={<HVACLanding />} />
                <Route path="/mep-engineers" element={<MEPEngineersLanding />} />
                <Route path="/hotels" element={<HotelsResorts />} />
                <Route path="/clevelandmarriott" element={<NoIndexRoute><MatterportOverlayDemo /></NoIndexRoute>} />
                <Route path="/site-survey-lod100" element={<SiteSurveyLOD100 />} />
                <Route path="/property-demo" element={<NoIndexRoute><PropertyDemo /></NoIndexRoute>} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/residential-faq" element={<ResidentialFAQ />} />
                <Route path="/commercial-faq" element={<CommercialFAQ />} />
                <Route path="/service-areas" element={<ServiceAreas />} />
                <Route path="/service-area/:citySlug" element={<ServiceAreaCity />} />
            <Route path="/free-headshot-realtors" element={<RealtorHeadshotOffer />} />
            <Route path="/home-protection" element={<HomeInsurance199 />} />
            <Route path="/revops" element={<RevOpsLanding />} />
            <Route path="/cost-segregation" element={<CostSegregation />} />
            <Route path="/retail-product-placement" element={<RetailProductPlacement />} />
            <Route path="/matterport-services-nashville-tn" element={<MatterportNashville />} />
            <Route path="/insurance-documentation-nashville-tn" element={<InsuranceDocumentationNashville />} />
            <Route path="/construction-documentation-nashville-tn" element={<ConstructionDocumentationNashville />} />
            <Route path="/real-estate-3d-tours-nashville-tn" element={<RealEstate3DToursNashville />} />
            <Route path="/storm-damage-documentation" element={<StormDamageDocumentation />} />
            <Route path="/project-status" element={<NoIndexRoute><ProjectStatus /></NoIndexRoute>} />
            <Route path="/commercial-leasing" element={<CommercialLeasing />} />
            <Route path="/disaster-documentation" element={<DisasterDocumentation />} />
            <Route path="/insurance" element={<Insurance />} />
            <Route path="/law-firms" element={<LawFirms />} />
            <Route path="/property-managers" element={<PropertyManagers />} />
            <Route path="/bot-debug" element={<NoIndexRoute><BotDebugTool /></NoIndexRoute>} />
            <Route path="/enterprise" element={<Enterprise />} />
            <Route path="/social-proof" element={<SocialProof />} />
            <Route path="/__design-system" element={<NoIndexRoute><DesignSystem /></NoIndexRoute>} />
            
            {/* Client Portal Routes */}
            <Route path="/portal/login" element={<NoIndexRoute><PortalLogin /></NoIndexRoute>} />
            <Route path="/portal/signup" element={<NoIndexRoute><PortalSignup /></NoIndexRoute>} />
            <Route path="/portal/forgot-password" element={<NoIndexRoute><PortalForgotPassword /></NoIndexRoute>} />
            <Route path="/portal/reset-password" element={<NoIndexRoute><PortalResetPassword /></NoIndexRoute>} />
            <Route path="/portal" element={<NoIndexRoute><ProtectedRoute><PortalDashboard /></ProtectedRoute></NoIndexRoute>} />
            <Route path="/portal/projects" element={<NoIndexRoute><ProtectedRoute><PortalProjects /></ProtectedRoute></NoIndexRoute>} />
            <Route path="/portal/projects/new" element={<NoIndexRoute><ProtectedRoute><PortalNewProject /></ProtectedRoute></NoIndexRoute>} />
            <Route path="/portal/projects/:id" element={<NoIndexRoute><ProtectedRoute><PortalProjectDetail /></ProtectedRoute></NoIndexRoute>} />
            <Route path="/portal/files" element={<NoIndexRoute><ProtectedRoute><PortalFiles /></ProtectedRoute></NoIndexRoute>} />
            <Route path="/portal/messages" element={<NoIndexRoute><ProtectedRoute><PortalMessages /></ProtectedRoute></NoIndexRoute>} />
            <Route path="/portal/invoices" element={<NoIndexRoute><ProtectedRoute><PortalInvoices /></ProtectedRoute></NoIndexRoute>} />
            <Route path="/portal/profile" element={<NoIndexRoute><ProtectedRoute><PortalProfile /></ProtectedRoute></NoIndexRoute>} />
            <Route path="/portal/leads" element={<NoIndexRoute><ProtectedRoute requiredRole="admin"><PortalLeads /></ProtectedRoute></NoIndexRoute>} />
            
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Router>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
